import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type ComponentProps,
  type Dispatch,
  type SetStateAction,
} from "react";
import {
  Box,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  DesktopDatePicker,
  LocalizationProvider,
  MobileDatePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimeField as MUITimeField } from "@mui/x-date-pickers/TimeField";
import { debug as debugFn } from "debug";
import { useField } from "formik";
import { useTranslation } from "react-i18next";

import { dayjs } from "@thalamos/common";
import type { Dayjs } from "@thalamos/common";
import type { TranslationKeyAndOptions } from "@thalamos/vision-common";

import { FormError } from "../FormError/FormError.js";
import { FormLabel } from "../FormLabel/FormLabel.js";

const debug = debugFn("vision-frontend:DateTimePicker");

const pairedFieldLabelSx = {
  display: "flex",
  alignItems: "flex-end",
  height: "100%",
} as const;

export type DateTimePickerOutput =
  | {
      value: null;
      isUnknown: true;
    }
  | {
      value: string;
      isUnknown: false;
    }
  | {
      value: string;
    }
  | "";

export type DateTimePickerProps = {
  fieldName: string;
  label?: TranslationKeyAndOptions;
  dateLabel: TranslationKeyAndOptions;
  timeLabel: TranslationKeyAndOptions;
  disablePast?: boolean;
  disableFuture?: boolean;
  canBeUnknown?: boolean;
  isEditable?: boolean;
} & Pick<ComponentProps<typeof DesktopDatePicker>, "autoFocus">;

const combineDateAndTime = (
  date: string | null,
  time: string | null,
): string | null => {
  if (!date || !time) {
    return null;
  }

  const parsedDate = dayjs(date, "YYYY-MM-DD", true);
  const parsedTime = dayjs(time, "HH:mm", true);

  if (parsedDate.isValid() && parsedTime.isValid()) {
    return parsedDate
      .hour(parsedTime.hour())
      .minute(parsedTime.minute())
      .toISOString();
  }

  return null;
};

export const DateTimePicker = ({
  label,
  fieldName,
  dateLabel,
  timeLabel,
  disablePast,
  disableFuture,
  canBeUnknown,
  isEditable,
  autoFocus,
}: DateTimePickerProps) => {
  const { t } = useTranslation();

  const [field, meta, helpers] = useField<DateTimePickerOutput>(fieldName);

  // This is where we store the individual date value as YYYY-MM-DD.
  const [date, setDate] = useState<string | null>(
    field.value === ""
      ? null
      : dayjs(field.value.value).isValid()
        ? dayjs(field.value.value).format("YYYY-MM-DD")
        : null,
  );

  // This is where we store the individual time value as HH:mm in "local time".
  const [time, setTime] = useState<string | null>(
    field.value === ""
      ? null
      : dayjs(field.value.value).isValid()
        ? dayjs(field.value.value).format("HH:mm")
        : null,
  );

  // Clean up the field value to be a structured object
  const fieldValue = useMemo(() => {
    const value =
      field.value === ""
        ? // Turn empty string into a structured object
          ({ value: null, isUnknown: false } as const)
        : "isUnknown" in field.value
          ? field.value.isUnknown
            ? // If "unknown", clear out the value
              ({ value: null, isUnknown: field.value.isUnknown } as const)
            : { value: field.value.value, isUnknown: field.value.isUnknown }
          : { value: field.value.value };

    debug("Cleaned up value", { value });
    return value;
  }, [field.value]);

  // The error could either come from Formik ("Invalid input") or from the schema
  // ({value: "string too short"}). We need to figure out which at runtime.
  const fieldError = useMemo(
    () => {
      const value =
        typeof meta.error === "string"
          ? meta.error
          : typeof meta.error === "object"
            ? (meta.error as { value: string } | undefined)?.value
            : undefined;

      return value === undefined ? undefined : t(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(meta.error)],
  );
  if (fieldError) {
    debug("fieldError", fieldError);
  }

  useEffect(
    () => {
      const runAsync = async () => {
        const combinedDateTime = combineDateAndTime(date, time);

        const newValue =
          // If the value is well structured and "unknown", then trust it and
          // use it going forward.
          "isUnknown" in fieldValue && fieldValue.isUnknown
            ? fieldValue
            : // If the value is not explicitly unknown, but the data is not valid,
              // then store it as "" which is Formik's way of representing null.
              combinedDateTime === null
              ? ""
              : canBeUnknown
                ? ({
                    value: combinedDateTime,
                    isUnknown: false as const,
                  } as const)
                : ({
                    value: combinedDateTime,
                  } as const);

        if (JSON.stringify(newValue) !== JSON.stringify(field.value)) {
          debug("Setting value", { combinedDateTime, newValue, fieldValue });
          await helpers.setValue(newValue);
          await helpers.setTouched(true);
        }
      };
      void runAsync();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      canBeUnknown,
      date,
      fieldName,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      "isUnknown" in fieldValue && fieldValue.isUnknown,
      fieldValue.value,
      helpers,
      time,
    ],
  );

  const handleUnknownChange = (event: ChangeEvent<HTMLInputElement>) => {
    const runAsync = async () => {
      const combinedDateAndTime = combineDateAndTime(date, time);

      const newValue = event.target.checked
        ? ({
            value: null,
            isUnknown: true,
          } as const)
        : combinedDateAndTime
          ? ({
              value: combinedDateAndTime,
              isUnknown: false,
            } as const)
          : "";

      debug("handleUnknownChange, Setting value", newValue);

      await helpers.setValue(newValue);
      await helpers.setTouched(true);
    };

    void runAsync();
  };

  return (
    <>
      {label && <FormLabel {...label} />}
      <Stack direction="column">
        {/*
         * Layout strategy: CSS Grid with named areas active only at sm+.
         *
         * Goal: on desktop (sm+), show labels and inputs in a two-column
         * arrangement where each label sits directly above its input and
         * the two columns are visually aligned:
         *
         *   [ Date label  ] [ Time label  ]
         *   [ Date input  ] [ Time input  ]
         *
         * On mobile (xs), the named gridArea assignments are omitted so CSS
         * Grid auto-placement takes over, rendering the four items in DOM
         * order as a single column:
         *
         *   [ Date label  ]
         *   [ Date input  ]
         *   [ Time label  ]
         *   [ Time input  ]
         *
         * DOM order deliberately matches the mobile visual order so that
         * keyboard and screen-reader navigation is meaningful at all
         * viewport sizes (WCAG 2.1 SC 1.3.2 — Meaningful Sequence).
         */}
        <Box
          data-testid={`formField-${fieldName}-layout`}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "repeat(2, minmax(0, 1fr))",
            },
            gridTemplateAreas: {
              sm: `
                "date-label time-label"
                "date-input time-input"
              `,
            },
            columnGap: 4,
            rowGap: 1,
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              ...pairedFieldLabelSx,
              gridArea: { sm: "date-label" },
              minWidth: 0,
            }}
            data-testid={`formField-${fieldName}-date-label`}
          >
            <FormLabel {...dateLabel} htmlFor={`${fieldName}-date-input`} />
          </Box>
          <Box
            sx={{ gridArea: { sm: "date-input" }, minWidth: 0 }}
            data-testid={`formField-${fieldName}-date`}
          >
            <DatePickerInput
              date={date}
              setDate={setDate}
              fieldName={fieldName}
              disabled={
                ("isUnknown" in fieldValue && fieldValue.isUnknown) ||
                isEditable === false
              }
              disablePast={disablePast}
              disableFuture={disableFuture}
              autoFocus={autoFocus}
            />
          </Box>
          <Box
            sx={{
              ...pairedFieldLabelSx,
              gridArea: { sm: "time-label" },
              minWidth: 0,
            }}
            data-testid={`formField-${fieldName}-time-label`}
          >
            <FormLabel {...timeLabel} htmlFor={`${fieldName}-time-input`} />
          </Box>
          <Box
            sx={{ gridArea: { sm: "time-input" }, minWidth: 0 }}
            data-testid={`formField-${fieldName}-time`}
          >
            <TimeField
              time={time}
              setTime={setTime}
              fieldName={fieldName}
              disabled={
                ("isUnknown" in fieldValue && fieldValue.isUnknown) ||
                isEditable === false
              }
            />
          </Box>
        </Box>
        {canBeUnknown && (
          <FormControlLabel
            control={
              <Switch
                checked={"isUnknown" in fieldValue && fieldValue.isUnknown}
                disabled={isEditable === false}
                onChange={handleUnknownChange}
                data-testid={`formField-${fieldName}-unknown`}
              />
            }
            label={t("common.unknown")}
          />
        )}
        {debug.enabled && (
          <>
            <Divider />
            <Typography sx={{ color: debug.color, fontFamily: "monospace" }}>
              {"field.value : "}
              {JSON.stringify(field.value)}
            </Typography>
            <Typography sx={{ color: debug.color, fontFamily: "monospace" }}>
              {"Treated like: "}
              {JSON.stringify(fieldValue)}
            </Typography>
            <Divider />
          </>
        )}
      </Stack>
      {fieldError && meta.touched && (
        <FormError data-testid="time-date-error" error={fieldError} />
      )}
    </>
  );
};

export const TimeField = ({
  time,
  setTime,
  fieldName,
  disabled,
}: {
  time: string | null;
  setTime: Dispatch<SetStateAction<string | null>>;
  fieldName: string;
  disabled?: boolean;
}) => {
  const handleTimeChange = useCallback(
    (value: Dayjs | null) => {
      const newTime = value?.isValid() ? value.format("HH:mm") : null;
      debug("Setting time to", newTime);
      setTime(newTime);
    },
    [setTime],
  );

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MUITimeField
          disabled={disabled}
          value={
            time && dayjs(time, "HH:mm", true).isValid()
              ? dayjs(time, "HH:mm", true)
              : null
          }
          onChange={handleTimeChange}
          ampm={false}
          slotProps={{
            textField: {
              fullWidth: true,
              inputProps: {
                id: `${fieldName}-time-input`,
                "data-testid": `formField-${fieldName}-time-input`,
              },
            },
          }}
        />
        {debug.enabled && (
          <Typography sx={{ color: debug.color, fontFamily: "monospace" }}>
            {JSON.stringify(time)}
          </Typography>
        )}
      </LocalizationProvider>
    </>
  );
};

const DatePickerInput = ({
  date,
  setDate,
  fieldName,
  disabled,
  disablePast,
  disableFuture,
  autoFocus,
}: {
  date: string | null;
  setDate: Dispatch<SetStateAction<string | null>>;
  fieldName: string;
  disabled?: boolean;
  disablePast?: boolean;
  disableFuture?: boolean;
  autoFocus?: boolean;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleDateChange = (value: Dayjs | null) => {
    if (value === null) {
      setDate(null);
    } else if (value.isValid()) {
      setDate(value.format("YYYY-MM-DD"));
    }
  };

  const DatePicker = isMobile ? MobileDatePicker : DesktopDatePicker;

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          disabled={disabled}
          disablePast={disablePast}
          disableFuture={disableFuture}
          value={date ? dayjs(date, "YYYY-MM-DD") : null}
          autoFocus={autoFocus}
          onChange={handleDateChange}
          format="DD/MM/YYYY"
          slotProps={{
            textField: {
              fullWidth: true,
              inputProps: {
                id: `${fieldName}-date-input`,
                "data-testid": `formField-${fieldName}-date-input`,
              },
            },
          }}
        />
        {debug.enabled && (
          <Typography sx={{ color: debug.color, fontFamily: "monospace" }}>
            {JSON.stringify(date)}
          </Typography>
        )}
      </LocalizationProvider>
    </>
  );
};
